import { useCallback, useEffect, useRef, useState } from 'react'
import {
  clampView,
  distance,
  FIT,
  fitSize,
  midpoint,
  panBy,
  zoomAt,
  type Point,
  type Size,
  type View,
} from '../domain/zoom.ts'
import { t } from '../i18n.ts'
import map from '../data/map.json'
import './FestivalMap.css'

/** How far a button, a key or a double tap moves the zoom in one go. */
const STEP = 1.6
const DOUBLE_TAP = 2.5

/** A tap is a pointer that went down and up in the same place, near enough. */
const TAP_SLOP = 12
const DOUBLE_TAP_MS = 300

/** What an arrow key nudges the map by, in frame pixels. */
const KEY_PAN = 60

const MAP_SIZE: Size = { width: map.width, height: map.height }

/**
 * The festival site plan, as published by the festival (`npm run fetch:map`).
 *
 * A picture, not data. Every stage, entrance, bar and toilet on it is drawn into the
 * artwork, so there is nothing here to lay out and nothing to keep in sync with the
 * programme — which is exactly why it can be a committed asset in the precache, and why
 * it is the one screen in the app that works with no signal and no account and no
 * localStorage. Standing in a field with forty thousand people is the moment a map is
 * worth anything, and it is also the moment the network is worth nothing.
 *
 * What it does need is zoom. The source is 9150×6627; fitted to a phone the stage names
 * are a few pixels tall, and a map you cannot read is a picture of a map. The gesture
 * arithmetic lives in `domain/zoom.ts`, where it can be tested; this component is the
 * wiring from pointers to that, plus the same three moves on buttons and keys so the
 * zoom is not reachable only by pinching.
 */
export function FestivalMap() {
  const [view, setView] = useState<View>(FIT)
  const frameRef = useRef<HTMLDivElement>(null)
  const [frame, setFrame] = useState<Size>({ width: 0, height: 0 })

  /*
   * Live pointers, by id, in frame coordinates.
   *
   * A ref rather than state on purpose: these change on every pointermove and nothing
   * renders from them directly — the view does. Putting them in state would re-render
   * the page once per frame per finger for no visible difference.
   */
  const pointers = useRef(new Map<number, Point>())
  const pinchDistance = useRef<number | null>(null)
  const tapStart = useRef<{ at: number; point: Point } | null>(null)
  const lastTap = useRef<{ at: number; point: Point } | null>(null)

  const fit = fitSize(MAP_SIZE, frame)

  /*
   * The frame's size, watched rather than measured once.
   *
   * Rotating a phone changes which axis the map fits by, and a view clamped to the old
   * frame would leave it parked outside the new one — visibly off-centre, with a gap
   * down one side that no gesture explains. Re-clamping on resize puts it back inside
   * whatever the frame just became.
   */
  useEffect(() => {
    const element = frameRef.current
    if (!element) return

    function measure() {
      const box = element!.getBoundingClientRect()
      setFrame({ width: box.width, height: box.height })
    }
    measure()

    // jsdom has no ResizeObserver, and the initial measure above is all a test needs.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setView((current) => clampView(current, fit, frame))
  }, [fit.width, fit.height, frame.width, frame.height]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Where a pointer is, relative to the frame's top-left. */
  const toFrame = useCallback((event: { clientX: number; clientY: number }): Point => {
    const box = frameRef.current?.getBoundingClientRect()
    return { x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0) }
  }, [])

  const zoomBy = useCallback(
    (factor: number, about?: Point) => {
      const centre = about ?? { x: frame.width / 2, y: frame.height / 2 }
      setView((current) => zoomAt(current, factor, centre, fit, frame))
    },
    [fit, frame],
  )

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    /*
     * Capture keeps the moves coming when a finger slides off the frame mid-drag, which
     * is most drags on a map that fills the screen. It is an improvement, not a
     * prerequisite — and it throws `NotFoundError` for a pointer the browser no longer
     * considers active, which happens for a touch released in the same tick and for
     * events synthesised by automation or assistive tech.
     *
     * Uncaught, that throw lands on the first line of the handler, so the pointer is
     * never recorded and every gesture after it is dead: pinch, drag and double tap all
     * stop working, with nothing on screen to say why. Exactly the shape of the sign-in
     * regression this repo already paid for once — a rejection nobody caught, read by
     * the user as an unresponsive control.
     */
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // Without capture a drag ends when the finger leaves the frame. Still a drag.
    }
    const point = toFrame(event)
    pointers.current.set(event.pointerId, point)
    if (pointers.current.size === 1) {
      /*
       * `performance.now()` rather than `event.timeStamp`, which is the more precise
       * value and the one a browser would give. The two share a time origin, so the
       * difference is under a frame — and this one can be pinned in a test, where the
       * alternative is a double-tap threshold measured against the wall clock and
       * therefore flaky in both directions on a loaded machine.
       */
      tapStart.current = { at: performance.now(), point }
    } else {
      // A second finger ends any tap in progress: a pinch is not a double tap.
      tapStart.current = null
      const [a, b] = [...pointers.current.values()]
      pinchDistance.current = a && b ? distance(a, b) : null
    }
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return
    const point = toFrame(event)
    pointers.current.set(event.pointerId, point)

    const active = [...pointers.current.values()]
    if (active.length === 1) {
      setView((current) => panBy(current, point.x - previous.x, point.y - previous.y, fit, frame))
      if (tapStart.current && distance(tapStart.current.point, point) > TAP_SLOP) {
        tapStart.current = null
      }
      return
    }

    const [a, b] = active
    if (!a || !b) return
    const gap = distance(a, b)
    const was = pinchDistance.current
    pinchDistance.current = gap
    // The first move after the second finger lands has nothing to compare against, and
    // a zero gap would divide into an infinity.
    if (!was || gap <= 0) return
    setView((current) => zoomAt(current, gap / was, midpoint(a, b), fit, frame))
  }

  function onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) pinchDistance.current = null

    const started = tapStart.current
    tapStart.current = null
    if (!started) return

    const point = toFrame(event)
    if (distance(started.point, point) > TAP_SLOP) return

    const at = performance.now()
    const earlier = lastTap.current
    const isDouble =
      earlier !== null &&
      at - earlier.at < DOUBLE_TAP_MS &&
      distance(earlier.point, point) <= TAP_SLOP * 2

    if (isDouble) {
      lastTap.current = null
      // Double tap toggles: in on the spot you tapped, and all the way back out again
      // from wherever you got to, which is the only gesture that undoes a lost pinch.
      if (view.scale > 1) setView(FIT)
      else zoomBy(DOUBLE_TAP, point)
      return
    }
    lastTap.current = { at, point }
  }

  function onPointerCancel(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) pinchDistance.current = null
    tapStart.current = null
  }

  function onWheel(event: React.WheelEvent<HTMLDivElement>) {
    // A trackpad pinch arrives as a ctrl-wheel; an ordinary wheel is a zoom here too,
    // because there is nothing on this page to scroll past.
    event.preventDefault()
    zoomBy(Math.exp(-event.deltaY * 0.002), toFrame(event))
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const pan: Record<string, [number, number]> = {
      ArrowLeft: [KEY_PAN, 0],
      ArrowRight: [-KEY_PAN, 0],
      ArrowUp: [0, KEY_PAN],
      ArrowDown: [0, -KEY_PAN],
    }
    const nudge = pan[event.key]
    if (nudge) {
      // Without this a keyboard user can zoom in and then never reach the edges: the
      // buttons change the scale, and nothing else moves the map.
      event.preventDefault()
      setView((current) => panBy(current, nudge[0], nudge[1], fit, frame))
      return
    }
    if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      zoomBy(STEP)
    } else if (event.key === '-') {
      event.preventDefault()
      zoomBy(1 / STEP)
    } else if (event.key === '0') {
      event.preventDefault()
      setView(FIT)
    }
  }

  const zoomed = view.scale > 1

  return (
    <div className="page map-page">
      <h1 className="sr-only">{t.map.title}</h1>

      <div
        ref={frameRef}
        className="map-page__frame"
        data-zoomed={zoomed || undefined}
        style={{
          // The gestures are ours; without this the browser pans and zooms the page out
          // from under them, and on iOS the pinch zooms the whole document instead.
          touchAction: 'none',
          // The frame takes the map's own shape rather than the screen's, so a landscape
          // map on a portrait phone is not marooned in 550px of empty mat. From the
          // snapshot, so a redrawn map of another shape brings its frame with it.
          '--map-ratio': `${map.width} / ${map.height}`,
        } as React.CSSProperties}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="group"
        aria-label={t.map.title}
      >
        <img
          className="map-page__image"
          src={map.file}
          width={map.width}
          height={map.height}
          alt={t.map.alt}
          // Otherwise a drag on desktop picks the image up as a file instead of panning.
          draggable={false}
          style={{ transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})` }}
        />
      </div>

      <div className="map-page__bar">
        <div className="map-page__controls">
          <button
            type="button"
            className="map-page__control"
            aria-label={t.map.zoomOut}
            disabled={!zoomed}
            onClick={() => zoomBy(1 / STEP)}
          >
            <MinusIcon />
          </button>
          <button
            type="button"
            className="map-page__control"
            aria-label={t.map.zoomIn}
            onClick={() => zoomBy(STEP)}
          >
            <PlusIcon />
          </button>
          <button
            type="button"
            className="map-page__reset"
            disabled={!zoomed}
            onClick={() => setView(FIT)}
          >
            {t.map.reset}
          </button>
        </div>

        {/* The hint goes quiet once it has been acted on: it is an instruction for the
            first visit, not a permanent label. */}
        {!zoomed && <p className="map-page__hint">{t.map.hint}</p>}

        <p className="map-page__credit">
          {t.map.credit} —{' '}
          <a href={map.source.page} target="_blank" rel="noreferrer noopener">
            {t.map.source}
          </a>
        </p>
      </div>
    </div>
  )
}

function PlusIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function MinusIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}
